/**
 * Store de dialogues de confirmation.
 *
 * Remplace les `window.confirm()` natifs (UI horrible, ne respecte pas le thème,
 * accessibilité limitée) par un dialog modal stylé.
 *
 * Usage :
 *   const confirm = useConfirmStore()
 *   if (await confirm.ask({ title: '...', message: '...', kind: 'danger' })) {
 *     // utilisateur a confirmé
 *   }
 */
import { defineStore } from 'pinia'

export type ConfirmKind = 'info' | 'warning' | 'danger'

export interface ConfirmOptions {
  title: string
  message?: string
  confirmLabel?: string
  cancelLabel?: string
  kind?: ConfirmKind
  /**
   * Si défini, l'utilisateur doit RETAPER exactement cette chaîne (ex: le nom
   * d'une campagne) pour activer le bouton de confirmation. Sert aux actions
   * destructrices irréversibles (suppression définitive).
   */
  requireText?: string
  /** Libellé du champ de saisie quand `requireText` est défini. */
  requireTextLabel?: string
  /**
   * Petites infos affichées dans le dialogue (ex: fiche contact d'un client
   * avant de l'appeler).
   */
  details?: { k: string; v: string }[]
  /**
   * Si vrai, le dialogue ne se ferme QUE par un bouton explicite (la croix ou
   * Annuler) : ni clic à l'extérieur, ni touche Échap. Pour les confirmations
   * importantes qu'on ne doit pas écarter par mégarde.
   */
  requireExplicitClose?: boolean
}

interface PendingConfirm extends ConfirmOptions {
  resolve: (value: boolean) => void
}

export const useConfirmStore = defineStore('confirm', {
  state: () => ({
    current: null as PendingConfirm | null,
  }),
  getters: {
    isOpen: (s) => s.current !== null,
  },
  actions: {
    ask(opts: ConfirmOptions): Promise<boolean> {
      // Si un confirm est déjà ouvert, on rejette le précédent en `false`
      // pour éviter qu'il soit oublié pour toujours.
      if (this.current) {
        this.current.resolve(false)
      }
      return new Promise<boolean>((resolve) => {
        this.current = { ...opts, resolve }
      })
    },
    resolveCurrent(value: boolean) {
      if (!this.current) return
      this.current.resolve(value)
      this.current = null
    },
  },
})
